import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Wallet, RefreshCw, LogOut, ExternalLink, ArrowDownUp, Repeat, Search, ChevronDown } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { Connection, VersionedTransaction } from "@solana/web3.js";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const RPC = "https://api.mainnet-beta.solana.com";

const MINTS = {
  SOL: "So11111111111111111111111111111111111111112",
  USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
  USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
};
const T_SOL = { mint: MINTS.SOL, symbol: "SOL", decimals: 9 };
const T_USDC = { mint: MINTS.USDC, symbol: "USDC", decimals: 6 };
const T_USDT = { mint: MINTS.USDT, symbol: "USDT", decimals: 6 };
const BASE_TOKENS = { [MINTS.SOL]: T_SOL, [MINTS.USDC]: T_USDC, [MINTS.USDT]: T_USDT };
const QUICK = [T_SOL, T_USDC, T_USDT];

const getPhantom = () => (typeof window !== "undefined" ? (window.phantom?.solana || window.solana) : null);

const waitForPhantom = async (tries = 25, delay = 150) => {
  for (let i = 0; i < tries; i++) {
    const p = getPhantom();
    if (p?.isPhantom) return p;
    await new Promise((r) => setTimeout(r, delay));
  }
  return null;
};

const inIframe = () => { try { return window.self !== window.top; } catch { return true; } };

const toAtomic = (value, decimals) => {
  if (!/^\d+(\.\d+)?$/.test(value)) return null;
  const [whole, frac = ""] = value.split(".");
  if (frac.length > decimals) return null;
  const n = BigInt(whole) * 10n ** BigInt(decimals) + BigInt((frac + "0".repeat(decimals)).slice(0, decimals) || "0");
  return n <= 0n ? null : n.toString();
};

const fromAtomic = (atomic, decimals) => {
  try { return Number(BigInt(atomic)) / 10 ** decimals; } catch { return 0; }
};

const decodeB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const shortMint = (m) => (m ? `${m.slice(0, 4)}...${m.slice(-4)}` : "");

// --- Searchable token picker (Jupiter) ---
const TokenPicker = ({ value, onChange, testid, t }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) { setResults([]); return; }
    const s = ++seq.current;
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/solana/token/search`, { params: { q: term } });
        if (s === seq.current) setResults(r.data.tokens || []);
      } catch { if (s === seq.current) setResults([]); }
      finally { if (s === seq.current) setLoading(false); }
    }, 350);
    return () => clearTimeout(h);
  }, [q, open]);

  const choose = (tk) => { onChange(tk); setOpen(false); setQ(""); setResults([]); };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
        className="flex items-center gap-1 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-2.5 py-1.5 text-sm font-semibold text-[var(--text-primary)] hover:border-purple-500"
      >
        {value?.icon && <img src={value.icon} alt="" className="w-4 h-4 rounded-full" />}
        {value?.symbol || shortMint(value?.mint)}
        <ChevronDown size={14} className="text-[var(--text-muted)]" />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-60 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] shadow-xl p-2" data-testid={`${testid}-panel`}>
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[var(--bg-tertiary)] mb-2">
            <Search size={14} className="text-[var(--text-muted)]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("sol_token_search_ph")}
              data-testid={`${testid}-input`}
              className="flex-1 bg-transparent text-sm text-[var(--text-primary)] focus:outline-none"
            />
          </div>
          {!q.trim() && (
            <div className="flex gap-1 mb-1">
              {QUICK.map((tk) => (
                <button key={tk.mint} onClick={() => choose(tk)}
                  className="flex-1 text-xs py-1 rounded-md bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-purple-300">
                  {tk.symbol}
                </button>
              ))}
            </div>
          )}
          <div className="max-h-56 overflow-y-auto">
            {loading ? (
              <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-purple-400" /></div>
            ) : results.length === 0 && q.trim() ? (
              <p className="text-xs text-[var(--text-muted)] px-2 py-3 text-center">{t("sol_token_none")}</p>
            ) : (
              results.map((tk) => (
                <button key={tk.mint} onClick={() => choose(tk)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] text-left">
                  {tk.icon ? <img src={tk.icon} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-[var(--bg-tertiary)]" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[var(--text-primary)] truncate">{tk.symbol}</div>
                    <div className="text-[10px] text-[var(--text-muted)] truncate">{tk.name || shortMint(tk.mint)}</div>
                  </div>
                  {tk.usdPrice != null && <div className="text-[10px] text-[var(--text-secondary)]">${Number(tk.usdPrice) < 0.01 ? Number(tk.usdPrice).toExponential(1) : Number(tk.usdPrice).toFixed(2)}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const PhantomModal = ({ open, onOpenChange, prefill, onPrefillConsumed }) => {
  const { t } = useLanguage();
  const [address, setAddress] = useState(localStorage.getItem("sol_wallet") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // --- swap state (token objects: {mint, symbol, decimals, icon}) ---
  const [fromToken, setFromToken] = useState(T_SOL);
  const [toToken, setToToken] = useState(T_USDC);
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [swapStatus, setSwapStatus] = useState("");
  const quoteSeq = useRef(0);
  const autoExecRef = useRef(false);

  useEffect(() => {
    if (open && prefill) {
      if (prefill.fromMint) setFromToken(BASE_TOKENS[prefill.fromMint] || { mint: prefill.fromMint, symbol: shortMint(prefill.fromMint), decimals: 9 });
      if (prefill.toMint) setToToken(BASE_TOKENS[prefill.toMint] || { mint: prefill.toMint, symbol: shortMint(prefill.toMint), decimals: 9 });
      setAmount(prefill.amount || "");
      autoExecRef.current = !!prefill.autoExecute;
      setQuote(null);
      onPrefillConsumed && onPrefillConsumed();
    }
  }, [open, prefill, onPrefillConsumed]);

  const loadBalances = useCallback(async (addr) => {
    if (!addr) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/solana/balances`, { params: { address: addr } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("sol_balance_failed")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { if (open && address) loadBalances(address); }, [open, address, loadBalances]);

  const pickFrom = (tk) => {
    if (tk.mint === toToken.mint) setToToken(fromToken);
    setFromToken(tk); setQuote(null);
  };
  const pickTo = (tk) => {
    if (tk.mint === fromToken.mint) setFromToken(toToken);
    setToToken(tk); setQuote(null);
  };
  const flip = () => { setFromToken(toToken); setToToken(fromToken); setQuote(null); };

  // debounced quote
  useEffect(() => {
    setQuote(null);
    const atomic = amount ? toAtomic(amount, fromToken.decimals) : null;
    if (!atomic || fromToken.mint === toToken.mint) return;
    const seq = ++quoteSeq.current;
    setQuoting(true);
    const h = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/solana/swap/quote`, { params: { inputMint: fromToken.mint, outputMint: toToken.mint, amount: atomic } });
        if (seq === quoteSeq.current) setQuote(r.data);
      } catch (e) {
        if (seq === quoteSeq.current) toast.error(e?.response?.data?.detail || t("sol_swap_quote_failed"));
      } finally {
        if (seq === quoteSeq.current) setQuoting(false);
      }
    }, 500);
    return () => clearTimeout(h);
  }, [amount, fromToken, toToken, t]);

  const fromBalance = () => {
    if (!data) return 0;
    if (fromToken.mint === MINTS.SOL) return data.sol || 0;
    const tk = (data.tokens || []).find((x) => x.mint === fromToken.mint);
    return tk ? tk.amount : 0;
  };

  const insufficient = amount && parseFloat(amount) > fromBalance();

  const doSwap = async () => {
    const ph = getPhantom();
    if (!ph?.isPhantom) { toast.error(t("sol_need_phantom")); return; }
    if (!quote) return;
    setSwapping(true);
    try {
      setSwapStatus(t("sol_swap_sign"));
      const build = await axios.post(`${API}/solana/swap/build`, { quoteResponse: quote, userPublicKey: address });
      const { swapTransaction, lastValidBlockHeight } = build.data;
      if (!swapTransaction) throw new Error("no_tx");
      const tx = VersionedTransaction.deserialize(decodeB64(swapTransaction));
      const { signature } = await ph.signAndSendTransaction(tx, { skipPreflight: false, preflightCommitment: "confirmed" });
      setSwapStatus(t("sol_swap_submitted"));
      const conn = new Connection(RPC, "confirmed");
      const conf = await conn.confirmTransaction(
        { signature, blockhash: tx.message.recentBlockhash, lastValidBlockHeight },
        "confirmed"
      );
      if (conf.value.err) throw new Error(JSON.stringify(conf.value.err));
      toast.success(
        <span>{t("sol_swap_confirmed")} · <a href={`https://solscan.io/tx/${signature}`} target="_blank" rel="noreferrer" className="underline">{t("sol_swap_view_tx")}</a></span>
      );
      setSwapStatus(""); setAmount(""); setQuote(null);
      loadBalances(address);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = detail || e?.message || "";
      if (status === 400 && detail) toast.error(detail);
      else if (/user reject|cancel|4001/i.test(msg)) toast.error(t("sol_swap_cancelled"));
      else toast.error(`${t("sol_swap_failed")}: ${String(msg).slice(0, 80)}`);
      setSwapStatus("");
    } finally { setSwapping(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const ph = await waitForPhantom();
      if (!ph) {
        if (inIframe()) toast.error(t("sol_iframe_note"));
        else toast.error(t("sol_need_phantom"));
        return;
      }
      const resp = await ph.connect();
      const addr = resp.publicKey.toString();
      localStorage.setItem("sol_wallet", addr);
      setAddress(addr);
      toast.success(t("sol_connected"));
    } catch (e) {
      const code = e?.code;
      const msg = e?.message || "";
      if (code === 4001 || /reject|cancel|declined/i.test(msg)) toast.error(t("sol_connect_cancelled"));
      else toast.error(`${t("sol_connect_failed")}: ${msg.slice(0, 120)}`);
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    try { await getPhantom()?.disconnect?.(); } catch (e) { /* ignore */ }
    localStorage.removeItem("sol_wallet");
    setAddress(""); setData(null); setQuote(null); setAmount("");
  };

  const short = (a) => (a ? `${a.slice(0, 4)}...${a.slice(-4)}` : "");
  const estOut = quote ? fromAtomic(quote.outAmount, toToken.decimals) : null;

  // Auto-execute a chat-triggered swap once the quote is ready & wallet connected.
  useEffect(() => {
    if (autoExecRef.current && quote && address && !swapping && !insufficient) {
      autoExecRef.current = false;
      doSwap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, address, swapping, insufficient]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[var(--bg-secondary)] border-[var(--border-color)] max-h-[90vh] overflow-y-auto" data-testid="phantom-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--text-primary)]">
            <Wallet size={20} className="text-purple-400" /> {t("sol_title")}
          </DialogTitle>
        </DialogHeader>

        {!address ? (
          <div className="space-y-3 py-2" data-testid="phantom-connect">
            <p className="text-sm text-[var(--text-secondary)]">{t("sol_connect_desc")}</p>
            {inIframe() && (
              <div className="space-y-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-200 text-xs" data-testid="phantom-iframe-warn">
                <div>{t("sol_iframe_note")}</div>
                <Button
                  onClick={() => window.open(window.location.href, "_blank", "noopener")}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-black h-8"
                  data-testid="phantom-open-newtab-btn"
                >
                  <ExternalLink size={14} className="mr-2" /> {t("sol_open_newtab")}
                </Button>
              </div>
            )}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-200 text-xs">
              {t("sol_noncustodial_note")}
            </div>
            <Button onClick={connect} disabled={connecting} className="w-full bg-purple-600 hover:bg-purple-500 text-white" data-testid="phantom-connect-btn">
              {connecting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Wallet size={16} className="mr-2" />}
              {t("sol_connect_btn")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <a href={`https://solscan.io/account/${address}`} target="_blank" rel="noreferrer"
                className="text-xs text-purple-300 hover:underline flex items-center gap-1" data-testid="phantom-address">
                {short(address)} <ExternalLink size={11} />
              </a>
              <div className="flex gap-2">
                <button onClick={() => loadBalances(address)} data-testid="phantom-refresh" className="text-[var(--text-muted)] hover:text-purple-400"><RefreshCw size={15} /></button>
                <button onClick={disconnect} data-testid="phantom-disconnect" className="text-[var(--text-muted)] hover:text-red-400"><LogOut size={15} /></button>
              </div>
            </div>

            {loading ? (
              <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-purple-400" /></div>
            ) : data ? (
              <>
                <div className="rounded-xl p-4 bg-gradient-to-br from-purple-600/20 to-indigo-600/10 border border-purple-500/30" data-testid="phantom-sol">
                  <div className="text-xs text-[var(--text-muted)]">SOL</div>
                  <div className="text-2xl font-bold text-[var(--text-primary)]">{data.sol}</div>
                  {data.sol_value_usd != null && (
                    <div className="text-sm text-[var(--text-secondary)]">
                      ≈ ${data.sol_value_usd.toLocaleString()} · ₺{(data.sol_value_try || 0).toLocaleString()}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold text-[var(--text-secondary)] mb-1.5">{t("sol_tokens")} ({data.tokens.length})</div>
                  {data.tokens.length === 0 ? (
                    <p className="text-xs text-[var(--text-muted)]">{t("sol_no_tokens")}</p>
                  ) : (
                    <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="phantom-tokens">
                      {data.tokens.map((tk) => (
                        <div key={tk.mint} className="flex justify-between items-center px-2.5 py-1.5 rounded-lg bg-[var(--bg-tertiary)] text-xs">
                          <a href={`https://solscan.io/token/${tk.mint}`} target="_blank" rel="noreferrer" className="text-[var(--text-primary)] hover:underline">{BASE_TOKENS[tk.mint]?.symbol || shortMint(tk.mint)}</a>
                          <span className="text-[var(--text-secondary)]">{tk.amount.toLocaleString(undefined, { maximumFractionDigits: 6 })}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* --- Swap --- */}
                <div className="rounded-xl p-3.5 bg-[var(--bg-tertiary)]/50 border border-[var(--border-color)] space-y-3" data-testid="phantom-swap">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
                    <Repeat size={15} className="text-purple-400" /> {t("sol_swap_title")}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-[var(--text-muted)]">{t("sol_swap_from")}</div>
                        <input
                          value={amount}
                          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                          inputMode="decimal"
                          placeholder="0.0"
                          data-testid="swap-amount-input"
                          className="w-full bg-transparent text-lg font-semibold text-[var(--text-primary)] focus:outline-none"
                        />
                      </div>
                      <TokenPicker value={fromToken} onChange={pickFrom} testid="swap-from-select" t={t} />
                    </div>

                    <div className="flex justify-center">
                      <button onClick={flip} data-testid="swap-flip-btn" className="p-1.5 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] text-purple-400 hover:text-purple-300">
                        <ArrowDownUp size={14} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-[var(--text-muted)]">{t("sol_swap_est")}</div>
                        <div className="text-lg font-semibold text-[var(--text-primary)] truncate" data-testid="swap-estimate">
                          {quoting ? <Loader2 size={16} className="animate-spin text-purple-400" /> : estOut != null ? estOut.toLocaleString(undefined, { maximumFractionDigits: 6 }) : "0.0"}
                        </div>
                      </div>
                      <TokenPicker value={toToken} onChange={pickTo} testid="swap-to-select" t={t} />
                    </div>
                  </div>

                  <div className="text-[10px] text-[var(--text-muted)]">{t("sol_swap_rate_note")}</div>
                  {swapStatus && <div className="text-xs text-purple-300">{swapStatus}</div>}

                  <Button
                    onClick={doSwap}
                    disabled={!quote || quoting || swapping || insufficient}
                    data-testid="swap-submit-btn"
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-50"
                  >
                    {swapping ? <Loader2 size={16} className="animate-spin mr-2" /> : <Repeat size={16} className="mr-2" />}
                    {insufficient ? t("sol_swap_insufficient") : t("sol_swap_btn")}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhantomModal;
